import { channelUrl, savedSearchChannelId } from './channelUrl';

/**
 * Single owner of the persisted "which channel is the reader on" slot.
 *
 * The reader remembers the last-viewed channel across pages and reloads in
 * localStorage. Two representation details used to leak into every call site:
 *
 * - the storage key (`selected_channel_id`), spelled inline by six modules
 *   (ReaderPage, ListsPage, StoriesPage, ArticleReadPage, AdminLayout, auth);
 * - the value encoding — either a plain channel id (`tldr`) or a saved-search
 *   channel id (`search:<uuid>`, see channelUrl.ts), with each writer choosing
 *   its own combination of raw string vs. savedSearchChannelId().
 *
 * Renaming the key, scoping it per-user, or changing the saved-search encoding
 * meant chasing all of those sites (and remembering that logout must clear the
 * slot). This module owns the key and the encoding choice; callers say what
 * they mean (remember this channel / remember this saved search / where was I
 * / forget) and never see the storage layout.
 *
 * Storage is injectable so the pure logic is testable under Jest's node
 * environment, where no localStorage global exists.
 */

export type ChannelStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const SELECTED_CHANNEL_KEY = 'selected_channel_id';

/** The remembered channel id, or '' when nothing has been remembered yet. */
export function selectedChannelId(store: ChannelStore = localStorage): string {
  return store.getItem(SELECTED_CHANNEL_KEY) || '';
}

/** The reader URL for the remembered channel (falls back to /tldr, see channelUrl). */
export function selectedChannelUrl(store: ChannelStore = localStorage): string {
  return channelUrl(selectedChannelId(store));
}

/** Remember a channel by its channel id (e.g. 'tldr' or an already-encoded saved-search id). */
export function rememberSelectedChannel(channelId: string, store: ChannelStore = localStorage): void {
  store.setItem(SELECTED_CHANNEL_KEY, channelId);
}

/** Remember a saved search as the selected channel, given its bare search/description id. */
export function rememberSelectedSavedSearch(searchId: string, store: ChannelStore = localStorage): void {
  rememberSelectedChannel(savedSearchChannelId(searchId), store);
}

/** Forget the remembered channel (e.g. on logout). */
export function clearSelectedChannel(store: ChannelStore = localStorage): void {
  store.removeItem(SELECTED_CHANNEL_KEY);
}
